/* tslint:disable:no-console max-line-length */
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {Guid} from 'guid-typescript';
import {firstValueFrom, NEVER, Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {Fault, ViewFault} from './fault';
import {FaultApiService} from './fault-api.service';
import {FaultData} from './fault-data';
import {FaultSyncService} from './sync/fault-sync.service';
import {MmChangeableModel} from '@docma-mm-core/model/mm-changeable.model';
import {FaultPatchService, faultUidsOfPatches} from './patch/fault-patch-service';
import {FaultPatch} from './patch/fault-patch';
// import * as assert from 'assert';
import {FaultDataLoaderService} from './fault-data-loader.service';
import {FaultPersistenceService} from './fault-persistence.service';
import {FaultOperationPathEnum} from './patch/fault-operation-path.enum';
import {MmSyncableModel} from '@docma-mm-core/model/mm-syncable.model';
import {FaultChangesCommitService} from './fault-changes-commit.service';
import {MmFaultChangePageService} from '../../mm-faults/mm-faults-view/mm-fault-right-pane/mm-fault-change-page.service';
import {MultiActivity} from '@docma-mm-core/activity/multi-activity';
import {FaultsEdit} from '@docma-mm-core/fault/faults-edit';
import {MmViewService} from '../../mm-view.service';
import {AttachmentDbService} from '@docma-mm-core/attachment/attachment-db.service';
import {ActivityDbService} from '@docma-mm-core/activity/db/activity-db.service';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import { OFFLINE_SERVICE } from '../../mm-offline/interfaces/offline-service.token';
import { IOfflineService } from '../../mm-offline/interfaces/offline-service.interface';
import { AttachmentApiService } from '@docma-mm-core/attachment/attachment-api.service';
import {ConnectivityService} from '@mydocma/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MmProjectLoaderService } from '../../mm-project/mm-project-loader.service';
import { User } from '@docma-mm-core/user/user';

@UntilDestroy()
@Injectable()
export class FaultService implements OnDestroy {

  private faultLoadingSubject = new Subject<string>();
  private faultLoadedSubject = new Subject<Fault>();
  public faultChangedSubject = new Subject<Fault>();
  private faultDeletedSubject = new Subject<Fault>();
  private faultCreatedSubject = new Subject<Fault>();

  onFaultLoading = this.faultLoadingSubject.asObservable();
  onFaultLoaded = this.faultLoadedSubject.asObservable();
  public onFaultChanged = this.faultChangedSubject.asObservable();
  onFaultDeleted = this.faultDeletedSubject.asObservable();
  onFaultCreated = this.faultCreatedSubject.asObservable();

  public currentPatchMode: ActivityPatchModeEnum;

  private user: User;

  constructor(private readonly persistenceService: FaultPersistenceService,
              private readonly commitService: FaultChangesCommitService,
              private readonly patchService: FaultPatchService,
              private readonly faultApiService: FaultApiService,
              private readonly faultDataLoader: FaultDataLoaderService,
              private readonly syncService: FaultSyncService,
              private readonly changeService: MmFaultChangePageService,
              private readonly viewService: MmViewService,
              private readonly attachmentDbService: AttachmentDbService,
              private readonly activityDbService: ActivityDbService,
              private readonly attachementApiService: AttachmentApiService,
              protected readonly connectivityService: ConnectivityService,
              private readonly projectLoaderService: MmProjectLoaderService,
              @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService
              ) {

      this.projectLoaderService.projectUserPermissions.pipe(untilDestroyed(this))
      .subscribe(u => this.user = u.data.User);
  }

  ngOnDestroy(): void {
  }


  async createNewFault(mydocmaUid: string, faultData?: FaultData): Promise<Fault> {
    const fault = new Fault();

   // assert(faultData?.Structure?.Id, "Can't create new fault.Structure node not provided");
    const changeData = new FaultData(faultData);
    changeData.CreatedOn = changeData.CreatedOn ?? new Date(Date.now());
    changeData.UniqueId = Guid.create().toString();
    if(this.user){
      changeData.Creator = {Id: this.user.Id, Account: this.user.Account, FirstName: this.user.FirstName, LastName: this.user.LastName};
    }

    fault.loadState = new LoadState({ data: new FaultData(), status: LoadStatusEnum.Loaded });
    fault.changeState = new ChangeState({ data: changeData, status: ChangeStatusEnum.New, date: changeData.CreatedOn });
    fault.syncState = new SyncState({ status: SyncStatusEnum.NotSync });
    this.faultCreatedSubject.next(fault);
    return fault;
  }



  async discardChanges(mydocmaUid: string, bpConnected: boolean, fault: Fault): Promise<Fault> {

    if (!fault.hasAnyChanges()) {
      return fault;
    }
    if (fault.changeState.status === ChangeStatusEnum.New) {
      await this.deleteFaults(mydocmaUid, bpConnected, [fault]);
      return fault;
    } else if (fault.changeState.status === ChangeStatusEnum.Changed) {
      fault.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
    }

    this.discardChangesInArray(fault.attachments);
    this.discardChangesInArray(fault.activities);
    fault.activities.forEach(a => this.discardChangesInArray(a.attachments));

    await this.persistenceService.saveOrRemoveIfNotNeeded(mydocmaUid, fault);

    this.faultChangedSubject.next(fault);
    return fault;
  }

  private discardChangesInArray(models: MmChangeableModel<any>[]) {
    for (let i = models.length - 1; i >= 0; --i) {
      const at = models[i];
      if (at.changeState.status === ChangeStatusEnum.Changed) {
        at.changeState = new ChangeState({ status: ChangeStatusEnum.NotChanged });
      } else if (at.changeState.status === ChangeStatusEnum.New) {
        models.splice(i, 1);
      }
    }
  }

  async deleteFaults(mydocmaUid: string, bpConnected: boolean, faults: Fault[]) {
    const newFaults   = faults.filter(f => f.changeState.status === ChangeStatusEnum.New);
    const savedFaults = faults.filter(f => f.changeState.status !== ChangeStatusEnum.New);

    newFaults.forEach(async fault => {
      /* TODO MMW-5194 */
      await this.persistenceService.removeAll(mydocmaUid, [fault.uid], null, null);
      this.faultDeletedSubject.next(fault);
    });

    if (savedFaults.length > 0) {
      await this.faultApiService.deleteFaults(savedFaults, bpConnected).toPromise();
      await Promise.all(savedFaults.map(async fault => {
        /* TODO MMW-5194 */
        this.persistenceService.removeAll(mydocmaUid, [fault.uid], null, null);
      }));

      savedFaults.forEach(fault => this.faultDeletedSubject.next(fault));
    }
  }

  async getOfflineFaults(mydocmaUid: string): Promise<Fault[]> {
    const faults = await this.persistenceService.getAll(mydocmaUid);

    if (faults) {
      const allPatches = await this.patchService.getAll(mydocmaUid);
      await this.patchService.loadSyncStates(allPatches, faults);
    }
    return faults;
  }

  async getFaults(mydocmaUid: string, viewFaults: ViewFault[], faultUids: string[], groupUids: string[], attachmentUids: string[]): Promise<{faults: Fault[], removedFromIDb: string[]}> {
    const faults = await this.persistenceService.getAll(mydocmaUid, faultUids);
    const allPatches = await this.patchService.getAll(mydocmaUid);

    await this.validateChangeState(mydocmaUid, allPatches, faults);
    await this.patchService.loadSyncStates(allPatches, faults);

    /* remove no longer needed faults */
    const removedFromIDb = await this.persistenceService.removeIfNotNeeded(mydocmaUid, faults.map(x => x.uid), groupUids, attachmentUids);

    return {faults, removedFromIDb};
  }

  async saveFaultsChanges(mydocmaUid: string, faultsEdit: FaultsEdit, autoCommit: boolean = true) {
    await this.persistenceService.saveFaultsEdit(mydocmaUid, faultsEdit);
    if (autoCommit) {
      const faultPatches = await this.commitService.commitFaultsEditChanges(mydocmaUid, faultsEdit);
      this.syncService.addPatchesToSyncQueue(mydocmaUid, faultPatches);
    }

    for (const fault of faultsEdit.faults.values()) {
      this.faultChangedSubject.next(fault);
    }
  }

  // async saveActivityChanges(mydocmaUid: string, activityEdit: ActivityEdit, autoCommit: boolean = true) {
  //   /* IN FIRST STEP ONLY SET FIRST FAULT TO CHANGED (NOT ALL OF GROUP) */
  //   // const faults: {fault: ViewFault, id: number}[] = [];
  //   // activityEdit.faults.forEach((fault: ViewFault, id: number) => faults.push({fault, id}));
  //
  //   // await Promise.all(faults.map(async x => this.faultChangedSubject.next(x.fault)));
  //
  //   await Promise.all(Object.keys(activityEdit.faults).map(async id => {
  //     this.faultChangedSubject.next(activityEdit.faults[id]);
  //   }));
  //
  //   /* save fault/file into index db */
  //   console.info('### committing faults to index db ###');
  //   const savedFaults = await this.persistenceService.saveActivityEdit(mydocmaUid, activityEdit);
  //   console.info('### finished committing faults to index db ###');
  //
  //   if (autoCommit) {
  //     /* save fault_patches into index db */
  //     const committedFaultPatches: Promise<FaultPatch[]>[] = [];
  //     const faultPatches = await this.commitService.commitActivityEditChanges(mydocmaUid, activityEdit);
  //     // this.syncService.addPatchesToSyncQueue(mydocmaUid, faultPatches);
  //   }
  // }

  async saveGroupChanges(mydocmaUid: string, multiActivity: MultiActivity, autoCommit: boolean = true) {
    this.currentPatchMode = multiActivity.patchMode;
    for (const item of multiActivity.entities) {
      this.faultChangedSubject.next(item.Fault);
    }

    /* save fault/file into index db */
    console.info('### committing faults to index db ###');
    const savedFaults = await this.persistenceService.saveMultiActivity(mydocmaUid, multiActivity);
    console.info('### finished committing faults to index db ###');

    console.info('### committing group to index db ###');
    await this.persistenceService.saveGroup(mydocmaUid, multiActivity.group);
    console.info('### finished committing group to index db ###');

    if (autoCommit) {
      /* save fault_patches into index db */
      const committedFaultPatches: Promise<FaultPatch[]>[] = [];
      const faultPatches = await this.commitService.commitGroupChanges(mydocmaUid, multiActivity.group, multiActivity);
      this.syncService.addPatchesToSyncQueue(mydocmaUid, faultPatches);
    }
  }

  async saveFaultChanges(mydocmaUid: string, fault: Fault, autoCommit: boolean = true) {

    await this.persistenceService.save(mydocmaUid, fault);
    if (autoCommit) {
      await this.commitService.commitFaultChanges(mydocmaUid, fault, false, null, null);
      await this.syncService.addFaultToSyncQueue(mydocmaUid, fault);
    }
    this.faultChangedSubject.next(fault);
  }

  public async validateChangeState(myDocmaUid: string, allPatches: FaultPatch[], faults: Fault[]) {
    console.info('### validateChangeState 01 ###');

    if (!allPatches) {
      allPatches = await this.patchService.getAll(myDocmaUid);
    }

    const patchedFaultUids: string[] = faultUidsOfPatches(allPatches);
    // const processedFaults: Fault[] = [];
    const updatedFaults: {[fuid: string]: Fault} = {};

    const faultsWithPatches = (!allPatches || allPatches.length === 0 ? [] : faults.filter(f => patchedFaultUids.includes(f.uid)));
    const faultsWithoutPatches = (!allPatches || allPatches.length === 0 ? faults : faults.filter(f => !patchedFaultUids.includes(f.uid)));

    const allPatchesByOUid = this.patchService.groupByOuid(allPatches);

    // const faultsWithAttachmentPatches = (!allPatches || allPatches.length === 0 ? [] : faults.filter(f => f.attachments.some(a => allPatchesByOUid.hasOwnProperty(a.uid)) ));
    const faultsWithoutAttachmentPatches = (!allPatches || allPatches.length === 0 ? faults : faults.filter(f => f.attachments.every(a => !allPatchesByOUid.hasOwnProperty(a.uid)) ));

    // const faultsWithActivitiesPatches = (!allPatches || allPatches.length === 0 ? [] : faults.filter(f => f.activities.some(a => allPatchesByOUid.hasOwnProperty(a.uid)) ));
    const faultsWithoutActivitiesPatches = (!allPatches || allPatches.length === 0 ? faults : faults.filter(f => f.activities.every(a => !allPatchesByOUid.hasOwnProperty(a.uid)) ));

    // const faultsWithActivityAttachmentsPatches = (!allPatches || allPatches.length === 0 ? [] : faults.filter(f => f.activities.some(a => allPatchesByOUid.hasOwnProperty(a.uid)) ));
    const faultsWithoutActivityAttachmentsPatches = (!allPatches || allPatches.length === 0 ? faults : faults.filter(f => f.activities.every(a => a.attachments.every(att => !allPatchesByOUid.hasOwnProperty(att.uid))) ));

    const pushChanged = (fault: Fault) => {
      if (!updatedFaults.hasOwnProperty(fault.uid)){
        updatedFaults[fault.uid] = fault;
      }
    };

    const saveChanged: string[] = [];
    const saveNotChanged: string[] = [];

    console.info('### validateChangeState 02 ###');

    await Promise.all(faultsWithPatches
      .filter(f => !f.changeState || f.changeState.status === ChangeStatusEnum.NotChanged)
      .map(async f => {
        if ([ChangeStatusEnum.NotChanged].findIndex(e => e == f.changeState.status) !== -1) {
          f.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.Changed });
          pushChanged(f);
        }
      }));

    /* set fault Changed if fault has pending patches + ChangeStatus is NotChanged */
    // const savedChangeStatesChanged = await this.persistenceService.saveChangeStates(myDocmaUid,
    //   loadChanged,
    //   new ChangeState<FaultData>({ status: ChangeStatusEnum.Changed }),
    //   [ChangeStatusEnum.NotChanged]);

    // for (const fault of faults.filter(f => savedChangeStatesChanged.findIndex(x => x.faultUid === f.uid) !== -1)) {
    //   fault.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.Changed });
    //   processedFaults.push(fault);
    // }

    console.info('### validateChangeState 03 ###');

    await Promise.all(faultsWithoutPatches
      .filter(f => f.changeState && f.changeState.status !== ChangeStatusEnum.NotChanged)
      .map(async f => {
        if ([ChangeStatusEnum.Changed, ChangeStatusEnum.New].findIndex(e => e == f.changeState.status) !== -1) {
          f.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged });
          pushChanged(f);
        }
      }));

    /* set fault NotChanged if fault has no pending patches + ChangeStatus is not NotChanged */
    // const savedChangeStatesNotChanged = await this.persistenceService.saveChangeStates(myDocmaUid,
    //   loadNotChanged,
    //   new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged }),
    //   [ChangeStatusEnum.Changed, ChangeStatusEnum.New]);


    await Promise.all(faultsWithoutAttachmentPatches
      .map(async f => {
        // SUPPORT REVERT CHANGES ON ATTACHMENT
        await Promise.all(f.attachments
          .filter(a => !allPatchesByOUid.hasOwnProperty(a.uid) && a.changeState.status !== ChangeStatusEnum.NotChanged)
          .map(async att => {
            att.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged });
            pushChanged(f);
            // await this.attachmentDbService.saveChangeState(myDocmaUid, f.uid, null, null, att.uid, new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged }));
          })
        );

        // SUPPORT REVERT NEW ATTACHMENT
        [...f.attachments]
          .filter(a => !allPatchesByOUid.hasOwnProperty(a.uid) && !a.id)  // todo: && allErrorPatchesByOUid.hasOwnProperty(a.uid)
          .forEach(att => {
            const i = f.attachments.findIndex(x => x.uid === att.uid);
            if (i !== -1) {
              f.attachments.splice(i, 1);
              pushChanged(f);
            }
          });

        // SUPPORT REVERT DELETED ATTACHMENT (CURRENTLY NOT NECESSARY BECAUSE ATTACHMENTS WILL BE DELETED DIRECTLY FAULT_PATCHES!)
      })
    );

    await Promise.all(faultsWithoutActivitiesPatches
      .map(async f => {
        // SUPPORT REVERT CHANGES ON ACTIVITY
        await Promise.all(f.activities
          .filter(a => !allPatchesByOUid.hasOwnProperty(a.uid) && a.changeState.status !== ChangeStatusEnum.NotChanged)
          .map(async act => {
            act.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged });
            pushChanged(f);
            // await this.activityDbService.saveChangeState(myDocmaUid, f.uid, null, null, act.uid, new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged }));
          })
        );

        // SUPPORT REVERT NEW ACTIVITY
        [...f.activities]
          .filter(a => !allPatchesByOUid.hasOwnProperty(a.uid) && !a.id)  // todo: && allErrorPatchesByOUid.hasOwnProperty(a.uid)
          .forEach(act => {
            const i = f.activities.findIndex(x => x.uid === act.uid);
            if (i !== -1) {
              f.activities.splice(i, 1);
              pushChanged(f);
            }
          });

        // SUPPORT REVERT DELETED ATTACHMENT (CURRENTLY NOT NECESSARY BECAUSE ACTIVITIES WILL BE DELETED DIRECTLY FAULT_PATCHES!)
      })
    );

    await Promise.all(faultsWithoutActivityAttachmentsPatches
      .map(async f => {
        // SUPPORT REVERT CHANGES ON ATTACHMENT
        f.activities.forEach(a => {
          a.attachments.forEach(att => {
            if (!allPatchesByOUid.hasOwnProperty(att.uid) && att.changeState.status !== ChangeStatusEnum.NotChanged) {
              att.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged });
              pushChanged(f);
            }
          });

          // SUPPORT REVERT NEW ATTACHMENT
          [...a.attachments]
            .filter(att => !allPatchesByOUid.hasOwnProperty(att.uid) && !att.id)  // todo: && allErrorPatchesByOUid.hasOwnProperty(a.uid)
            .forEach(att => {
              const i = a.attachments.findIndex(x => x.uid === att.uid);
              if (i !== -1) {
                a.attachments.splice(i, 1);
                pushChanged(f);
              }
            });

          // SUPPORT REVERT DELETED ATTACHMENT (CURRENTLY NOT NECESSARY BECAUSE ACTIVITIES WILL BE DELETED DIRECTLY FAULT_PATCHES!)
        });
      })
    );

    await Promise.all(Object.keys(updatedFaults).map(async fuid => {
      const f = updatedFaults[fuid];
      // const found = savedChangeStatesNotChanged.find(x => x.faultUid === f.uid);
      // if (found) {
      //   f.changeState = found.changeState;
      // }
      await this.persistenceService.save(myDocmaUid, f);
    }));


    console.info('### validateChangeState 04 ###');

    // for (const fault of faults.filter(f => savedChangeStatesNotChanged.findIndex(x => x.faultUid === f.uid) !== -1)) {
    //   fault.changeState = new ChangeState<FaultData>({ status: ChangeStatusEnum.NotChanged });
    //   processedFaults.push(fault);
    // }

    return Object.values(updatedFaults);
  }

  async loadFaults(mydocmaUid: string, structureId: number, faults: ViewFault[], forceReload: boolean = false, additionalInformations: number = 11, cancelation?: Observable<any>): Promise<Fault[]> {
    const faultUniqueIds = [];
    for (const f of faults) {
      if (f?.id != null &&
        ((forceReload && f.changeState.status !== ChangeStatusEnum.New) ||
        (f.loadState.status !== LoadStatusEnum.Loaded && f.loadState.status !== LoadStatusEnum.Loading)
        || !f.loadState?.data?.UniqueId)) {
        f.loadState.status = LoadStatusEnum.Loading;
        faultUniqueIds.push(f.uid);
      }
      // else {
      //   if (await this.persistenceService.updateSyncStatus(f, allFaults)) {
      //     this.faultLoadedSubject.next(f);
      //   }
      // }
    }

    if (faultUniqueIds.length !== 0) {
      faultUniqueIds.forEach(f => this.faultLoadingSubject.next(f));

      const allPatches = await this.patchService.getAll(mydocmaUid);

      this.faultApiService.searchFaults(
        structureId,
        null,
        faultUniqueIds.length,
        0,
        null,
        null,
        null,
        [],
        [],
        [],
        faultUniqueIds,
        additionalInformations,
        cancelation
        )
        .pipe(
          takeUntil(cancelation || NEVER ),
          map(data => data && data.Data ? data.Data : [])
        )
        .subscribe(async loaded => {
          const loadedFaultDatas: FaultData[] = [];
          const loadedFaults: ViewFault[] = [];

          await Promise.all(faults.map(async f => {
            const found: FaultData = loaded.find(l => l.Id === f.id);
            if (found) {
              const isOffline = await firstValueFrom(this.connectivityService.appIsInOfflineMode);
              if (isOffline) {
                await this.patchService.loadSyncStates(allPatches, [f]);
                await this.persistenceService.updateSyncStatus(f);
              }

              this.faultDataLoader.deepLoadFromData(f, found);
              loadedFaultDatas.push(found);
              loadedFaults.push(f);
            }
          }));

          loadedFaults.forEach(f => this.faultLoadedSubject.next(f));
        });
    }
    return faults;
  }

  async loadEditFaults(mydocmaUid: string, structureId: number, faults: Fault[]): Promise<FaultData[]> {

    const faultUniqueIds = faults.map(f => f.uid);

    if (faultUniqueIds.length !== 0) {
      return this.faultApiService.searchFaults(
        structureId,
        0,
        faultUniqueIds.length,
        0,
        null,
        null,
        null,
        [
          'Number1',
          'Number2',
          'Desc1_1',
          'Desc1_2',
          'Desc1_3',
          'Desc2_1',
          'Desc2_2',
          'Desc2_3',
          'Text1',
          'Text2',
          'Text3',
          'Date1',
          'Date2',
          'Date3',
          'Craft',
          'Company',
          'Order',
          'ContactCO',
          'ContactCT',
          'ContactSC',
          'Class1',
          'Class2',
          'Class3',
          'Class4',
          'UserDefined1',
          'UserDefined2',
          'CostsCompany',
          'CostsOrder',
          'Costs1',
          'Costs2',
          'Costs3',
          'Costs4',
          'Costs5',
          'Opt1',
          'Opt2',
          'Opt3',
          'Opt4',
          'Opt5',
          'Opt6',
          'Opt7',
          'Opt8',
          'Opt9',
          'IsVisibleForCO',
          'IsVisibleForCT',
          'Structure',
          'Attribute'
        ],
        [],
        [],
        faultUniqueIds,
        0)
        .pipe(
          map(data => data && data.Data ? data.Data : [])
        ).toPromise();
    }
    return null;
  }

  async removeFaultsFromDownload(faults: Fault[]) {
    const viewFault = faults[0] as ViewFault;
    if (!viewFault?.project){
      return;
    }


    throw new Error('Use file download service directly');

    //TODO:

    // for (const fault of faults){
    //   await this.faultDownloadService.remove(viewFault?.project.MyDocmaUid, fault);
    // }
  }

  async loadOfflineFault(mydocmaUid: string, faultUid: string): Promise<Fault> {
    const fault = await this.persistenceService.get(mydocmaUid, faultUid);
    if (fault) {
      this.faultLoadedSubject.next(fault);
    }
    return fault;
  }
}


